import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useThemeMode } from "@common/hooks";
import { InputProps } from "@common/types";
import { Input } from "./Input";

declare let grecaptcha: any;

declare global {
  interface Window {
    captchaInit?: () => void;
  }
}

let recaptchaLoader: Promise<void> | null = null;
const challengeClassName = "nh-recaptcha-challenge";
const challengeFrameClassName = "nh-recaptcha-challenge__frame";
const recaptchaScriptUrl =
  "https://www.google.com/recaptcha/api.js?onload=captchaInit&render=explicit";

function isRecaptchaReady() {
  return (
    typeof grecaptcha !== "undefined" &&
    typeof grecaptcha.render === "function" &&
    typeof grecaptcha.getResponse === "function" &&
    typeof grecaptcha.reset === "function"
  );
}

function loadRecaptchaScript() {
  if (isRecaptchaReady()) {
    return Promise.resolve();
  }

  if (recaptchaLoader) {
    return recaptchaLoader;
  }

  recaptchaLoader = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${recaptchaScriptUrl}"]`,
    );

    window.captchaInit = () => {
      resolve();
    };

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener(
        "error",
        () => {
          recaptchaLoader = null;
          reject(new Error("Failed to load reCAPTCHA script"));
        },
        { once: true },
      );

      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = recaptchaScriptUrl;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      recaptchaLoader = null;
      reject(new Error("Failed to load reCAPTCHA script"));
    };

    document.head.appendChild(script);
  });

  return recaptchaLoader;
}

function syncChallengeLayout() {
  const frames = document.querySelectorAll<HTMLIFrameElement>(
    'iframe[src*="recaptcha/api2/bframe"]',
  );

  frames.forEach((frame) => {
    frame.classList.add(challengeFrameClassName);
    frame.parentElement?.classList.add(challengeClassName);
  });
}

function cleanupChallengeLayout() {
  document
    .querySelectorAll(`.${challengeFrameClassName}`)
    .forEach((frame) => frame.classList.remove(challengeFrameClassName));
  document
    .querySelectorAll(`.${challengeClassName}`)
    .forEach((container) => container.classList.remove(challengeClassName));
}

export type RecaptchaHandle = {
  getValue: () => string;
  reset: () => void;
};

export const Recaptcha = forwardRef<
  RecaptchaHandle,
  {
    onChange: (value: string) => void;
  } & InputProps
>(function Recaptcha({ onChange, ...rest }, ref) {
  const theme = useThemeMode();

  const [loaded, setLoaded] = useState(isRecaptchaReady);

  const inputRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const getValue = () => {
    if (
      widgetIdRef.current !== null &&
      typeof grecaptcha !== "undefined" &&
      typeof grecaptcha.getResponse === "function"
    ) {
      return grecaptcha.getResponse(widgetIdRef.current) || "";
    }

    return "";
  };

  useImperativeHandle(ref, () => ({
    getValue,
    reset: () => {
      if (
        widgetIdRef.current !== null &&
        typeof grecaptcha !== "undefined" &&
        typeof grecaptcha.reset === "function"
      ) {
        grecaptcha.reset(widgetIdRef.current);
      }

      onChangeRef.current("");
    },
  }));

  useEffect(() => {
    let isMounted = true;

    loadRecaptchaScript()
      .then(() => {
        if (isMounted) {
          setLoaded(true);
        }
      })
      .catch(() => {
        if (isMounted) {
          setLoaded(false);
          onChangeRef.current("");
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (loaded && inputRef.current && widgetIdRef.current === null) {
      widgetIdRef.current = grecaptcha.render(inputRef.current, {
        sitekey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
        theme,
        callback: (value: string) => onChangeRef.current(value || ""),
        "expired-callback": () => onChangeRef.current(""),
        "error-callback": () => onChangeRef.current(""),
      });
    }
  }, [loaded, theme]);

  useEffect(() => {
    const sync = () => requestAnimationFrame(syncChallengeLayout);
    const observer = new MutationObserver(sync);

    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    sync();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
      cleanupChallengeLayout();
    };
  }, []);

  return (
    <Input {...rest}>
      <div
        className="g-recaptcha flex origin-center scale-90 justify-center"
        ref={inputRef}
      />
    </Input>
  );
});
